import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { SessionContext, StepType } from '@esign/contracts'
import { Note, Stepper } from '@esign/ui'
import {
  SignedScreen,
  STEP_REGISTRY,
  stepperItems,
  type StepError,
  type StepNotice,
  type StepProps,
  type UserContractItem,
} from '@esign/steps'
import { SigningApiError, submitStep } from './api.js'
import { MiddlewareApiError, middlewareApi } from './middleware-api.js'
import { signingUrl } from './config.js'

import { markDemoSigned, DEMO_SESSION_ID } from './demo-session.js'

/**
 * Orchestrator.
 *
 * Toàn bộ việc của file này: tra registry theo `current_step`, gom kết quả submit,
 * và điều hướng theo `next_step` mà BE trả về. Nó không biết có những loại bước nào,
 * không biết bước nào đứng trước bước nào, và không có một dòng nào nhắc tên partner.
 *
 * Thêm một loại bước mới = thêm một dòng trong `STEP_REGISTRY` + một phần tử trong config.
 * File này không đổi.
 */
export function App({ context }: { context: SessionContext }) {
  const { session, flow } = context
  const firstStepType = flow.steps[0]?.type ?? 'info'
  const [isSigned, setIsSigned] = useState(() => session.status === 'signed')
  const [forceSignNew, setForceSignNew] = useState(() => isForceSignNew(session.id))
  const [forceStep, setForceStep] = useState<StepType>(() => readForceSignNewStep(session.id) ?? firstStepType)
  const forceSignNewRef = useRef(forceSignNew)
  const forceStepRef = useRef(forceStep)
  const showSignedScreenRef = useRef(false)
  forceSignNewRef.current = forceSignNew
  forceStepRef.current = forceStep

  const effectiveStatus = isSigned ? 'signed' : session.status
  const activeStepType = forceSignNew ? forceStep : session.current_step
  const sdkUrl =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('dev_ekyc_sdk_url') : null
  const savedIdentity = readSavedIdentity(session.id)
  const activeIndex = flow.steps.findIndex((s) => s.type === activeStepType)
  const viewContext: SessionContext = forceSignNew
    ? {
        ...context,
        session: {
          ...session,
          current_step: activeStepType,
          status: 'pending',
          completed_steps: flow.steps.slice(0, Math.max(0, activeIndex)).map((s) => s.type),
        },
        flow: {
          ...flow,
          steps: flow.steps.map((s) =>
            s.type === 'ekyc' && sdkUrl
              ? { ...s, config: { ...s.config, webview_url: sdkUrl } }
              : s,
          ),
        },
        subject: {
          ...context.subject,
          ...(savedIdentity.email ? { email: String(savedIdentity.email) } : {}),
          ...(savedIdentity.phone || savedIdentity.phone_number
            ? { phone: String(savedIdentity.phone || savedIdentity.phone_number) }
            : {}),
        },
      }
    : context
  const step = viewContext.flow.steps.find((s) => s.type === activeStepType)

  const [error, setError] = useState<StepError | null>(null)
  const [notice, setNotice] = useState<StepNotice | null>(null)
  const [busy, setBusy] = useState(false)
  const submittingRef = useRef(false)

  /**
   * `useCallback` không phải để tối ưu: effect nghe `postMessage` của bước eKYC phụ thuộc
   * vào `submit`. Nếu hàm này đổi identity mỗi lần render thì listener bị gỡ ra gắn lại
   * liên tục, và một message đến đúng khe hở đó sẽ mất.
   *
   * `submittingRef` chặn bấm "Tiếp tục" liên tục (iframe FPT gửi nhiều `ekyc_results`)
   * trước khi `busy` kịp render — không đưa `busy` vào deps để tránh gỡ listener.
   */
  const submit = useCallback(
    (payload: Record<string, unknown>) => {
      if (!step) return
      if (submittingRef.current) return

      // link_account success confirmation: user bấm "Xác thực danh tính" → đi tiếp.
      if (step.type === 'link_account' && payload['proceed'] && notice?.code === 'LINKED') {
        const next = notice.data['next_step']
        if (typeof next === 'string') location.assign(signingUrl(session.id, next as never))
        return
      }

      submittingRef.current = true
      setBusy(true)
      setError(null)

      void submitStep(session.id, step.type, payload)
        .then((result) => {
          if (!result.ok) {
            submittingRef.current = false
            setBusy(false)
            setNotice(null)
            setError({ code: result.error_code ?? 'UNKNOWN', data: result.data })
            // Backend báo tab đang giữ state cũ. Tải lại context là cách duy nhất đúng.
            if (result.error_code === 'STEP_OUT_OF_ORDER') location.reload()
            return
          }

          // Figma 1:65097 — giữ màn hình success trước khi sang eKYC.
          if (step.type === 'link_account' && result.next_step) {
            submittingRef.current = false
            setBusy(false)
            setError(null)
            setNotice({ code: 'LINKED', data: { next_step: result.next_step } })
            return
          }

          if (result.next_step) {
            persistForceSignNew(session.id, result.next_step)
            setForceSignNew(true)
            setForceStep(result.next_step)
            submittingRef.current = false
            setBusy(false)
            return
          }

          clearForceSignNew(session.id)
          setForceSignNew(false)
          markDemoSigned(session.id)
          sessionStorage.setItem(`esign_just_signed_${session.id}`, '1')
          try {
            history.pushState(null, '', signingUrl(session.id, 'signed'))
          } catch {
            // ignore
          }
          fetchedContractsRef.current = false
          setHasExistingContracts(true)
          setIsSigned(true)
          submittingRef.current = false
          setBusy(false)
        })
        .catch((reason: unknown) => {
          submittingRef.current = false
          setBusy(false)
          setNotice(null)
          const message = reason instanceof Error && reason.message ? reason.message : undefined
          const code =
            reason instanceof MiddlewareApiError
              ? reason.code
              : reason instanceof SigningApiError
                ? reason.code
                : 'NETWORK_ERROR'
          setError({
            code,
            data: message ? { message } : {},
          })
        })
    },
    [context, notice, session.id, step],
  )

  useEffect(() => {
    const exitWebview = (status: 'cancelled' | 'signed' = 'cancelled') => {
      clearForceSignNew(session.id)
      setForceSignNew(false)
      try {
        if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'esign:exit',
              status,
              session_id: session.id,
            }),
          )
        } else {
          window.history.back()
        }
      } catch {
        // ignore
      }
    }

    const handleGoBack = () => {
      // Màn list / ký thành công: Back đóng webview về App, không đi lùi step eKYC
      if (showSignedScreenRef.current) {
        exitWebview(effectiveStatus === 'signed' ? 'signed' : 'cancelled')
        return
      }

      if (forceSignNewRef.current) {
        const types = flow.steps.map((s) => s.type)
        const currentIndex = types.indexOf(forceStepRef.current)
        if (currentIndex > 0) {
          const prevStep = types[currentIndex - 1]
          if (!prevStep) return
          persistForceSignNew(session.id, prevStep)
          setForceStep(prevStep)
          return
        }
        // Bước 1 khi ký lại: đóng webview về màn App (thiết lập tài khoản), không về list HĐ
        exitWebview()
        return
      }

      const currentStep = session.current_step
      const steps = flow.steps.map((s) => s.type)
      const currentIndex = steps.indexOf(currentStep)

      if (currentIndex > 0) {
        const prevStep = steps[currentIndex - 1]
        location.assign(signingUrl(session.id, prevStep as never))
      } else {
        exitWebview()
      }
    }

    ;(window as any).esignGoBack = handleGoBack
    ;(window as any).goBack = handleGoBack

    const onMessage = (event: MessageEvent) => {
      try {
        let data = event.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch {
            // raw string
          }
        }
        if (
          data === 'goBack' ||
          data?.type === 'esign:goBack' ||
          data?.type === 'goBack' ||
          data?.action === 'goBack'
        ) {
          handleGoBack()
        }
      } catch (err) {
        console.warn('goBack message error:', err)
      }
    }

    window.addEventListener('message', onMessage)
    document.addEventListener('message', onMessage as any)

    return () => {
      window.removeEventListener('message', onMessage)
      document.removeEventListener('message', onMessage as any)
      delete (window as any).esignGoBack
      delete (window as any).goBack
    }
  }, [flow.steps, session.current_step, session.id])

  const [hasExistingContracts, setHasExistingContracts] = useState<boolean | null>(null)
  const [contractsList, setContractsList] = useState<UserContractItem[] | null>(null)
  const fetchedContractsRef = useRef(false)

  useEffect(() => {
    if (fetchedContractsRef.current) return
    fetchedContractsRef.current = true
    
    middlewareApi.getContracts()
      .then((res: any) => {
        const list = res?.data?.contracts || []
        setContractsList(list)
        if (list.length > 0 || effectiveStatus === 'signed') {
          setHasExistingContracts(true)
        } else {
          setHasExistingContracts(false)
        }
      })
      .catch(() => {
        setContractsList([])
        setHasExistingContracts(effectiveStatus === 'signed')
      })
  }, [effectiveStatus])

  const signingInProgress = forceSignNew || isForceSignNew(session.id)
  const showSignedScreen =
    !step || (!signingInProgress && (effectiveStatus === 'signed' || hasExistingContracts === true))
  showSignedScreenRef.current = showSignedScreen

  return (
    <div className="shell">
      <main className="shell__body">
        {hasExistingContracts === null && !signingInProgress && effectiveStatus !== 'signed' ? (
          <div className="flow-card">
            <div className="flow-card__content">
              <Note>Đang kiểm tra thông tin hợp đồng…</Note>
            </div>
          </div>
        ) : showSignedScreen ? (
          <SignedScreen
            context={context}
            initialContracts={contractsList || undefined}
            onSignNew={() => {
              submittingRef.current = false
              setError(null)
              setNotice(null)
              persistForceSignNew(session.id, firstStepType)
              setForceStep(firstStepType)
              setForceSignNew(true)
              setIsSigned(false)
            }}
          />
        ) : (
          <div className={`flow-card flow-card--${step.type}`}>
            <div className="flow-card__header">
              <div className="flow-card__title-row">
                <h1
                  className="flow-card__title"
                  aria-label={step.type === 'info' ? 'Thông tin' : undefined}
                >
                  Ký hợp đồng hợp tác
                </h1>
              </div>
              <p className="flow-card__subtitle">
                Từ ngày 02/05/2024, bạn cần ký hợp đồng điện tử để thỏa mãn yêu cầu thanh toán.
              </p>
              <div className="flow-card__stepper">
                <Stepper items={stepperItems(viewContext)} />
              </div>
            </div>

            <div className="flow-card__content">
              {/*
               * `key={step.type}`: state cục bộ của một bước (giá trị form, sheet đang mở) không
               * được dính sang bước sau. Suspense là vì bước eKYC được `lazy()`.
               */}
              <Suspense fallback={<Note>Đang tải bước xác thực…</Note>}>
                <StepView
                  step={step}
                  context={viewContext}
                  error={error}
                  notice={notice}
                  busy={busy}
                  submit={submit}
                  key={step.type}
                />
              </Suspense>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function StepView(props: StepProps) {
  const Component = STEP_REGISTRY[props.step.type]
  return <Component {...props} />
}

function readSavedIdentity(sessionId: string): Record<string, unknown> {
  if (typeof sessionStorage === 'undefined' || !sessionId) return {}
  const str =
    sessionStorage.getItem(`dev_identity_data_${sessionId}`) ||
    (sessionId === DEMO_SESSION_ID ? sessionStorage.getItem('dev_identity_data') : null)
  if (!str) return {}
  try {
    const parsed = JSON.parse(str)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const forceSignNewKey = (sessionId: string) => `esign_force_sign_new_${sessionId}`
const forceSignNewStepKey = (sessionId: string) => `esign_force_sign_new_step_${sessionId}`
const FORCE_SIGN_NEW_GLOBAL = 'esign_force_sign_new'
const FORCE_SIGN_NEW_STEP_GLOBAL = 'esign_force_sign_new_step'

function isForceSignNew(sessionId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false
  return (
    sessionStorage.getItem(forceSignNewKey(sessionId)) === '1' ||
    sessionStorage.getItem(FORCE_SIGN_NEW_GLOBAL) === '1'
  )
}

function readForceSignNewStep(sessionId: string): StepType | null {
  if (typeof sessionStorage === 'undefined') return null
  const value =
    sessionStorage.getItem(forceSignNewStepKey(sessionId)) ||
    sessionStorage.getItem(FORCE_SIGN_NEW_STEP_GLOBAL)
  return value ? (value as StepType) : null
}

function persistForceSignNew(sessionId: string, step: StepType): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(forceSignNewKey(sessionId), '1')
  sessionStorage.setItem(forceSignNewStepKey(sessionId), step)
  sessionStorage.setItem(FORCE_SIGN_NEW_GLOBAL, '1')
  sessionStorage.setItem(FORCE_SIGN_NEW_STEP_GLOBAL, step)
}

function clearForceSignNew(sessionId: string): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(forceSignNewKey(sessionId))
  sessionStorage.removeItem(forceSignNewStepKey(sessionId))
  sessionStorage.removeItem(FORCE_SIGN_NEW_GLOBAL)
  sessionStorage.removeItem(FORCE_SIGN_NEW_STEP_GLOBAL)
}
